import { isCapDevice } from './publicWifiRrmPlanUi';

export function parseRssiDbm(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

function deviceAssociationMatchKeys(device) {
  const keys = new Set();
  (device?.radios || []).forEach((radio) => {
    const mac = radio.mac?.toLowerCase()?.replace(/:/g, '');
    if (mac) keys.add(mac);
  });
  const serial = device?.serial_number?.toLowerCase();
  if (serial) {
    keys.add(serial);
    keys.add(serial.replace(/:/g, ''));
  }
  return keys;
}

/** Parent AP hears child (association RSSI on parent). */
export function findApRssiInAssociations(childDevice, parentDevice) {
  const deviceIdentifiers = deviceAssociationMatchKeys(childDevice);
  if (deviceIdentifiers.size === 0) return null;

  const searchInterfaces = (interfaces) => {
    if (!interfaces) return null;
    for (const iface of interfaces) {
      const associations = iface.Associations || iface.associations;
      if (!associations) continue;
      for (const assoc of associations) {
        const station = assoc.Station || assoc.station;
        if (!station) continue;
        const stationId = station.toLowerCase().replace(/:/g, '');
        if (deviceIdentifiers.has(stationId) || deviceIdentifiers.has(station.toLowerCase())) {
          return parseRssiDbm(assoc.RSSI ?? assoc.rssi);
        }
      }
    }
    return null;
  };

  return searchInterfaces(parentDevice?.private_wifi?.interfaces || parentDevice?.private_wifi?.Interfaces)
    ?? searchInterfaces(parentDevice?.public_wifi?.interfaces || parentDevice?.public_wifi?.Interfaces);
}

/** Child STA hears parent (uplink block or STA-mode interface RSSI). */
export function getChildStaRssi(childDevice) {
  const uplinkRssi = parseRssiDbm(childDevice?.uplink?.rssi);
  if (uplinkRssi != null) return uplinkRssi;

  const ifaces = childDevice?.private_wifi?.interfaces || childDevice?.private_wifi?.Interfaces;
  if (!ifaces) return null;
  const staIface = ifaces.find((iface) => (iface.Mode || iface.mode)?.toLowerCase() === 'station');
  return parseRssiDbm(staIface?.RSSI ?? staIface?.rssi);
}

export function getBackhaulConnectionRssi(childDevice, parentDevice) {
  return {
    apRssi: findApRssiInAssociations(childDevice, parentDevice),
    staRssi: getChildStaRssi(childDevice),
  };
}

function findParentDevice(device, allDevices) {
  if (!device?.parent_serial || !allDevices) return null;
  const key = device.parent_serial.toLowerCase();
  return allDevices.find((d) => d.serial_number?.toLowerCase() === key) ?? null;
}

/**
 * Backhaul RSSI for map filtering. kind is "ap" (parent hears child) or "sta" (child hears parent).
 */
export function getDeviceBackhaulRssi(device, kind, allDevices) {
  if (!device || (kind !== 'ap' && kind !== 'sta')) return null;
  if (kind === 'sta') {
    return getChildStaRssi(device);
  }
  const parent = findParentDevice(device, allDevices);
  if (!parent) return null;
  return findApRssiInAssociations(device, parent);
}

/**
 * Returns true when the device meets the map signal filter.
 * kind: "ap" | "sta" | "both" — both requires AP and STA RSSI >= minRssi.
 * CAP devices always pass (no backhaul parent link to score).
 */
export function devicePassesSignalFilter(device, kind, minRssi, allDevices) {
  if (isCapDevice(device)) return true;
  if (!Number.isFinite(minRssi)) return true;
  if (kind === 'ap') {
    const rssi = getDeviceBackhaulRssi(device, 'ap', allDevices);
    return rssi != null && rssi >= minRssi;
  }
  if (kind === 'sta') {
    const rssi = getDeviceBackhaulRssi(device, 'sta', allDevices);
    return rssi != null && rssi >= minRssi;
  }
  if (kind === 'both') {
    const apRssi = getDeviceBackhaulRssi(device, 'ap', allDevices);
    const staRssi = getDeviceBackhaulRssi(device, 'sta', allDevices);
    return apRssi != null && staRssi != null && apRssi >= minRssi && staRssi >= minRssi;
  }
  return true;
}
